import { Col, Row, Button } from "antd";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import { useState, useMemo } from "react";

function MonthlyUtilization(props) {
  const { langIndex } = useSelector((x) => x.app);
  const { screenSize, monthlyTotal, monthlyUtilizations } = props;
  
  // State to track if zero values should be filtered out
  const [filterZeroValues, setFilterZeroValues] = useState(false);

  // Calculate filtered data excluding zero values when filter is enabled
  const filteredData = useMemo(() => {
    if (filterZeroValues) {
      return monthlyUtilizations.filter(item => {
        const utilization = parseFloat(item.utilization) || 0;
        return utilization > 0;
      });
    }
    return monthlyUtilizations;
  }, [monthlyUtilizations, filterZeroValues]);

  // Calculate total from actual utilization values (not monthlyTotal)
  const actualTotal = useMemo(() => {
    return monthlyUtilizations.reduce((sum, item) => {
      const utilization = item && item.utilization ? parseFloat(item.utilization) || 0 : 0;
      return sum + utilization;
    }, 0);
  }, [monthlyUtilizations]);

  // Calculate total from filtered data
  const activeTotal = useMemo(() => {
    const total = filteredData.reduce((sum, item) => {
      const utilization = item && item.utilization ? parseFloat(item.utilization) || 0 : 0;
      return sum + utilization;
    }, 0);
    
    return total;
  }, [filteredData]);

  // Calculate active days count
  const activeDaysCount = useMemo(() => {
    return filteredData.length;
  }, [filteredData.length]);

  // Use appropriate total based on whether filter is enabled
  const displayTotal = useMemo(() => {
    if (!filterZeroValues) {
      // When filter is off, use the original monthlyTotal for consistency
      return monthlyTotal || 0;
    }
    // When filter is on, use the sum of non-zero values
    return activeTotal;
  }, [monthlyTotal, activeTotal, filterZeroValues]);

  // Use original 30 days when filter is off, otherwise use filtered days count
  const displayDaysCount = useMemo(() => {
    if (!filterZeroValues) return 30;
    return Math.max(activeDaysCount, 1); // Ensure at least 1 to avoid division by zero
  }, [activeDaysCount, filterZeroValues]);

  // Calculate cumulative average trend line (running average up to each day)
  const trendLineData = useMemo(() => {
    const result = [];
    let cumulativeSum = 0;
    let activeCount = 0;

    monthlyUtilizations.forEach((item, index) => {
      const value = parseFloat(item.utilization) || 0;
      const shouldInclude = !filterZeroValues || value > 0;

      // Only include values that pass the filter in the cumulative average
      if (shouldInclude) {
        cumulativeSum += value;
        activeCount += 1;
      }

      // Calculate running average (only if we have at least one active value)
      const runningAverage = activeCount > 0 ? cumulativeSum / activeCount : 0;

      result.push({
        x: item.day,
        y: runningAverage
      });
    });

    return result;
  }, [monthlyUtilizations, filterZeroValues]);

  const series = [
    {
      name: 'Utilization',
      type: 'column',
      data: monthlyUtilizations.map((x, index) => ({
        x: x.day,
        y: parseFloat(x.utilization) || 0,
        fillColor: '#36a3f0'
      })),
    },
    {
      name: 'Average',
      type: 'line',
      data: trendLineData,
    },
  ];

  const options = {
    chart: {
      type: "line",
      toolbar: { show: false },
      width: 100,
      zoom: {
        enabled: false
      },
    },

    plotOptions: {
      bar: {
        columnWidth: "60%",
        borderRadius: 4,
        distributed: true, // This allows individual colors per bar
      },
    },
    dataLabels: {
      enabled: false,
    },
    
    stroke: {
      width: [0, 2], // 0 for bars, 2 for average line
      curve: 'straight', // Straight line for average
    },

    fill: {
      type: ['solid', 'solid'],
      colors: ['#36a3f0', '#ff4444'] // Blue for bars, Red for average line
    },

    labels: monthlyUtilizations.map((x) => x.day),
    xaxis: {
      show: true,
      labels: {
        style: {
          colors: '#ffffff',
        },
        formatter: function (val, timestamp) {
          return val;
        },
      },
    },

    yaxis: {
      show: false,
    },

    tooltip: {
      fixed: {
        enabled: false,
      },
      x: {
        show: false,
      },
      y: {
        title: {
          formatter: function (seriesName) {
            return "";
          },
        },
        formatter: function (val, opts) {
          const seriesIndex = opts.seriesIndex;
          
          // Format to 1 decimal place
          const formattedVal = parseFloat(val).toFixed(1);
          
          if (seriesIndex === 0) {
            // Bar series
            return formattedVal + ' hrs';
          } else {
            // Average line series
            return 'Avg: ' + formattedVal + ' hrs';
          }
        },
      },
      marker: {
        show: false,
      },
    },

    colors: ['#36a3f0', '#ff4444'], // Blue for bars, Red for average line
    states: {
      active: {
        filter: {
          type: 'none'
        }
      },
      hover: {
        filter: {
          type: 'lighten',
          value: 0.15
        }
      }
    },
    legend: {
      show: false
    }
  };

  return (
    <Row className="msd-chart-container">
      <Col span={18} className="msd-chart-title">
        Monthly Spindle Utilization
      </Col>
      <Col span={6} style={{ textAlign: 'right', paddingTop: '5px' }}>
        <Button 
          size="small" 
          type={filterZeroValues ? "primary" : "default"}
          onClick={() => setFilterZeroValues(!filterZeroValues)}
          style={{ 
            backgroundColor: filterZeroValues ? '#36a3f0' : 'transparent',
            borderColor: '#36a3f0',
            color: filterZeroValues ? '#fff' : '#36a3f0'
          }}
        >
          Filter Zero Values
        </Button>
      </Col>
      <Col span={24} className="msd-chart-subtitle">
        Trailing 30 Days Total {filterZeroValues && `(Zero values filtered)`}
      </Col>
      <Col span={6} className="msd-chart-left">
        <div>
          <div style={{ fontSize: '14px', color: '#aaa', marginBottom: '2px' }}>
            Average Hours/Day
          </div>
          <div className="msd-chart-hrs">
            {isNaN(displayTotal / displayDaysCount) ? '0.0' : (displayTotal / displayDaysCount).toFixed(1)} hrs
          </div>
          <div style={{ fontSize: '14px', color: '#aaa', marginBottom: '2px', marginTop: '10px' }}>
            Average Utilization
          </div>
          <div className="msd-chart-percentage">
            {isNaN(displayTotal / 24 / displayDaysCount * 100) ? '0.0' : (displayTotal / 24 / displayDaysCount * 100).toFixed(1)} %
          </div>
        </div>
      </Col>
      <Col span={18} className="msd-chart-right">
        <ReactApexChart
          options={options}
          series={series}
          type="line"
          height={180}
        />
      </Col>
    </Row>
  );
}

export default MonthlyUtilization;
